import React, { useState, useEffect } from 'react';
import api from '../services/api'; // Importa o serviço de API configurado

// Error Boundary para autenticação
class AuthErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error('Auth crash:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-6 text-center">
          <p className="text-red-600 font-semibold">
            Algo deu errado. Recarregue a página.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

// Componente de Ícone para as funcionalidades
const FeatureIcon = ({ children }) => (
  <div className="bg-purple-100 text-purple-600 rounded-full h-12 w-12 flex items-center justify-center">
    {children}
  </div>
);

// Componente do Card de Plano
const PlanCard = ({ plan, price, features, popular = false }) => (
  <div className={`border rounded-lg p-6 flex flex-col ${popular ? 'border-purple-500 relative shadow-lg' : 'border-gray-300'}`}>
    {popular && <span className="bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full absolute -top-3 right-4">MAIS POPULAR</span>}
    <h3 className="text-xl font-bold text-gray-800">{plan}</h3>
    <p className="text-3xl font-bold my-4">{price}</p>
    <ul className="space-y-3 text-gray-600 flex-grow">
      {features.map((feature, index) => (
        <li key={index} className="flex items-center">
          <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
          {feature}
        </li>
      ))}
    </ul>
    <button className={`mt-6 w-full py-2 rounded-lg font-semibold transition-transform transform hover:scale-105 ${popular ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-800'}`}>
      Escolher Plano
    </button>
  </div>
);

// --- NOVO: Componente do Modal de Autenticação ---
const AuthModal = ({ show, onClose, onLoginSuccess, initialView = 'login' }) => {
  // Estados para gerenciar as diferentes views do modal
  const [currentView, setCurrentView] = useState(initialView); // 'login', 'register', 'verifyEmail', 'resetPassword', 'confirmResetPassword'
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState(''); // Para a senha temporária no confirm reset
  const [newPassword, setNewPassword] = useState(''); // Para a nova senha no confirm reset
  const [confirmNewPassword, setConfirmNewPassword] = useState(''); // Para confirmar a nova senha no confirm reset
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [useInviteCode, setUseInviteCode] = useState(false);
  const [inviteCode, setInviteCode] = useState('');

  // Atualiza o currentView quando initialView mudar ou quando o modal é aberto (show muda de false para true)
  useEffect(() => {
    if (show) {
      setCurrentView(initialView);
      setError('');
      setSuccessMessage('');

      // Recuperar o email salvo no sessionStorage ao abrir o modal
      const savedEmail = sessionStorage.getItem('verifyEmail');
      if (savedEmail) {
        setEmail(savedEmail);
      }
    }
  }, [initialView, show]);

  const handleRequestVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const { data } = await api.post('/api/users/register/request', { nome, email, senha }, {
        timeout: 15000  // 15 segundos de timeout
      });

      // Verifica se há um código de verificação na resposta (para desenvolvimento)
      if (data.verificationCode) {
        setVerificationCode(data.verificationCode);
        setSuccessMessage('Código de verificação gerado. Verifique o código abaixo e digite no campo.');
      } else {
        setSuccessMessage(data.message || 'Código de verificação enviado para o seu email.');
      }

      // Armazenar o email no sessionStorage para persistência entre sessões
      sessionStorage.setItem('verifyEmail', email);

      setCurrentView('verifyEmail');
      setError('');
      setSuccessMessage('');
    } catch (err) {
      console.error('Erro na solicitação de verificação:', err);
      // Tratar diferentes tipos de erro para evitar tela branca
      if (err.code === 'ECONNABORTED') {
        setError('Tempo de solicitação expirado. Por favor, verifique sua conexão e tente novamente.');
      } else if (err.response) {
        // Handle specific error codes from backend
        const errorResponse = err.response.data;
        let errorMessage = errorResponse?.message || `Erro ${err.response.status}: Não foi possível enviar o código de verificação.`;

        if (errorResponse?.error) {
          switch (errorResponse.error) {
            case 'USER_EXISTS':
              errorMessage = 'Utilizador já existe com este e-mail.';
              break;
            case 'MISSING_FIELDS':
              errorMessage = 'Por favor, preencha todos os campos (nome, email, senha).';
              break;
            default:
              errorMessage = errorResponse.message || `Erro ${err.response.status}: Não foi possível enviar o código de verificação.`;
          }
        }

        setError(errorMessage);
      } else if (err.request) {
        // Erro de requisição (sem resposta do servidor)
        setError('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.');
      } else {
        // Outro tipo de erro
        setError(err.message || 'Ocorreu um erro inesperado. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRegistrationRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const requestData = { nome, email };
      if (useInviteCode && inviteCode.trim()) {
        requestData.inviteCode = inviteCode.trim();
      }

      // Using the new automatic registration endpoint
      const { data } = await api.post('/api/users/register', requestData, {
        timeout: 15000  // 15 segundos de timeout
      });

      // Show success message about temporary password being sent
      setSuccessMessage('Senha temporária enviada no seu e-mail, efetue o login para entrar');

      // Clear form fields after successful submission
      setNome('');
      setEmail('');
      setUseInviteCode(false);
      setInviteCode('');
    } catch (err) {
      console.error('Erro ao enviar solicitação de registro:', err);
      // Tratar diferentes tipos de erro para evitar tela branca
      if (err.code === 'ECONNABORTED') {
        setError('Tempo de solicitação expirado. Por favor, verifique sua conexão e tente novamente.');
      } else if (err.response) {
        // Handle specific error codes from backend
        const errorResponse = err.response.data;
        let errorMessage = errorResponse?.message || `Erro ${err.response.status}: Não foi possível enviar a solicitação de registro.`;

        if (errorResponse?.error) {
          switch (errorResponse.error) {
            case 'USER_EXISTS':
              errorMessage = 'Utilizador já existe com este e-mail.';
              break;
            case 'REQUEST_EXISTS':
              errorMessage = 'Já existe uma solicitação de registro pendente para este e-mail.';
              break;
            case 'MISSING_FIELDS':
              errorMessage = 'Por favor, preencha todos os campos (nome, email).';
              break;
            default:
              errorMessage = errorResponse.message || `Erro ${err.response.status}: Não foi possível enviar a solicitação de registro.`;
          }
        }

        setError(errorMessage);
      } else if (err.request) {
        // Erro de requisição (sem resposta do servidor)
        setError('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.');
      } else {
        // Outro tipo de erro
        setError(err.message || 'Ocorreu um erro inesperado. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    // Validar se o email está disponível
    if (!email) {
      setError('Sessão expirada. Por favor, cadastre-se novamente.');
      setCurrentView('register');
      sessionStorage.removeItem('verifyEmail');
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.post('/api/users/register/verify', {
        email,
        verificationCode,
        nome,
        senha,
        plano: 'comum'
      }, {
        timeout: 15000  // 15 segundos de timeout
      });
      // Limpar o email do sessionStorage após sucesso
      sessionStorage.removeItem('verifyEmail');
      onLoginSuccess(data);
      // Fechar o modal após o sucesso do login
      handleClose();
    } catch (err) {
      const errorResponse = err.response?.data;
      let errorMessage = errorResponse?.message || 'Ocorreu um erro. Tente novamente.';

      // Handle specific error codes from backend
      if (errorResponse?.error) {
        switch (errorResponse.error) {
          case 'INVALID_CODE':
            errorMessage = 'Código de verificação inválido.';
            break;
          case 'CODE_EXPIRED':
            errorMessage = 'Código de verificação expirado. Por favor, solicite um novo código.';
            break;
          case 'ALREADY_VERIFIED':
            errorMessage = 'Este email já foi verificado.';
            break;
          case 'VERIFICATION_NOT_FOUND':
            errorMessage = 'Solicitação de verificação não encontrada. Por favor, solicite um novo código.';
            break;
          case 'MISSING_FIELDS':
            errorMessage = 'Por favor, preencha todos os campos.';
            break;
          case 'INVALID_USER_DATA':
            errorMessage = 'Dados de usuário inválidos.';
            break;
          default:
            errorMessage = errorResponse.message || 'Ocorreu um erro. Tente novamente.';
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { data } = await api.post('/api/users/login', { email, senha }, {
        timeout: 15000  // 15 segundos de timeout
      });

      // Only call onLoginSuccess if the response contains a valid token
      if (data.token) {
        onLoginSuccess(data);
      } else {
        // If no token is present, treat as an error even if HTTP status was 200
        setError(data.message || 'E-mail ou senha inválidos.');
        setSenha('');
      }
    } catch (err) {
      // Only show error message without closing the modal
      const status = err.response?.status;
      const errorResponse = err.response?.data;
      let message = errorResponse?.message || 'Ocorreu um erro. Tente novamente.';

      // Handle specific error codes from backend
      if (errorResponse?.error) {
        switch (errorResponse.error) {
          case 'MISSING_FIELDS':
            message = 'E-mail e senha são obrigatórios. Por favor, preencha todos os campos.';
            break;
          default:
            message = errorResponse.message || 'Ocorreu um erro. Tente novamente.';
        }
      } else {
        // Provide more user-friendly error messages based on the error status
        if (status === 400) {
          message = 'E-mail e senha são obrigatórios. Por favor, preencha todos os campos.';
          // For validation errors, don't clear fields so user can see what they entered
        } else if (status === 401) {
          message = errorResponse?.message || 'E-mail ou senha inválidos.'; // Use the message from the backend ('E-mail ou senha inválidos.')
          // For authentication errors, clear only the password field
        } else if (status === 500) {
          message = 'Erro no servidor. Tente novamente mais tarde.';
          // For server errors, clear the password field
        } else {
          message = errorResponse?.message || 'Ocorreu um erro. Tente novamente.';
        }
      }

      setError(message);
      // For other errors, clear the password field
      setSenha('');
    } finally {
      setLoading(false);
    }
  };

  // Função para solicitar a redefinição de senha
  const handleRequestPasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const { data } = await api.post('/api/users/reset-password/request', { email }, {
        timeout: 30000  // Aumentar o timeout para 30 segundos para dar tempo ao envio do e-mail
      });

      setSuccessMessage(data.message || 'Senha temporária enviada para o seu email.');
      // Navegar automaticamente para a view de confirmação
      setCurrentView('confirmResetPassword');
    } catch (err) {
      // Melhor tratamento de erro para distinguir entre diferentes tipos de erro
      if (err.code === 'ECONNABORTED') {
        // Timeout - pode ser que o backend ainda tenha enviado o e-mail, mesmo após timeout
        setSuccessMessage('A senha temporária está sendo enviada. Por favor, verifique seu e-mail em alguns minutos. Se não receber, tente novamente mais tarde.');
        setCurrentView('confirmResetPassword');
      } else if (err.response) {
        // Handle specific error codes from backend
        const errorResponse = err.response.data;
        let errorMessage = errorResponse?.message || 'Ocorreu um erro. Tente novamente.';

        if (errorResponse?.error) {
          switch (errorResponse.error) {
            case 'USER_NOT_FOUND':
              errorMessage = 'Nenhum usuário encontrado com este e-mail. Verifique o endereço e tente novamente.';
              break;
            case 'MISSING_EMAIL':
              errorMessage = 'Por favor, forneça um endereço de e-mail.';
              break;
            default:
              errorMessage = errorResponse.message || 'Ocorreu um erro. Tente novamente.';
          }
        } else {
          // Erro de resposta do servidor
          const status = err.response.status;

          if (status === 404) {
            errorMessage = 'Nenhum usuário encontrado com este e-mail. Verifique o endereço e tente novamente.';
          } else if (status === 500) {
            errorMessage = 'Erro no servidor. Por favor, tente novamente mais tarde.';
          } else {
            errorMessage = errorResponse?.message || 'Ocorreu um erro. Tente novamente.';
          }
        }

        setError(errorMessage);
      } else {
        // Erro de rede ou outro tipo de erro
        setError('Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Função para confirmar a redefinição de senha
  const handleConfirmPasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    if (newPassword !== confirmNewPassword) {
      setError('As novas senhas não coincidem.');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('A nova senha deve ter pelo menos 6 caracteres.');
      setLoading(false);
      return;
    }

    try {
      const { data } = await api.post('/api/users/reset-password/confirm', {
        email,
        temporaryPassword,
        newPassword
      }, {
        timeout: 15000  // 15 segundos de timeout
      });
      // Login automático após redefinição bem-sucedida
      onLoginSuccess(data);
    } catch (err) {
      const errorResponse = err.response?.data;
      let errorMessage = errorResponse?.message || 'Ocorreu um erro. Tente novamente.';

      // Handle specific error codes from backend
      if (errorResponse?.error) {
        switch (errorResponse.error) {
          case 'INVALID_TOKEN':
            errorMessage = 'Senha temporária inválida.';
            break;
          case 'TOKEN_EXPIRED':
            errorMessage = 'Senha temporária expirada. Por favor, solicite uma nova.';
            break;
          case 'NO_RESET_REQUEST':
            errorMessage = 'Nenhuma solicitação de redefinição de senha encontrada ou já foi utilizada.';
            break;
          case 'USER_NOT_FOUND':
            errorMessage = 'Nenhum usuário encontrado com este e-mail.';
            break;
          case 'INVALID_PASSWORD':
            errorMessage = 'A nova senha deve ter pelo menos 6 caracteres.';
            break;
          case 'MISSING_FIELDS':
            errorMessage = 'Por favor, preencha todos os campos (email, senha temporária, nova senha).';
            break;
          default:
            errorMessage = errorResponse.message || 'Ocorreu um erro. Tente novamente.';
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNome('');
    setEmail('');
    setSenha('');
    setVerificationCode('');
    setTemporaryPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setError('');
    setSuccessMessage('');
    setUseInviteCode(false);
    setInviteCode('');
  };

  const handleClose = () => {
    resetForm();
    setCurrentView('login'); // Resetar para a view de login ao fechar
    setError('');
    setSuccessMessage('');
    // Limpar o email do sessionStorage ao fechar o modal
    sessionStorage.removeItem('verifyEmail');
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">
            {currentView === 'login' && 'Login'}
            {currentView === 'register' && 'Cadastro Gratuito'}
            {currentView === 'verifyEmail' && 'Verificar Email'}
            {currentView === 'resetPassword' && 'Recuperar Senha'}
            {currentView === 'confirmResetPassword' && 'Definir Nova Senha'}
          </h2>
          <button type="button" onClick={handleClose} className="text-gray-500 hover:text-gray-800 text-2xl">&times;</button>
        </div>
        
        {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
        {successMessage && <p className="bg-green-100 text-green-700 p-3 rounded mb-4">{successMessage}</p>}
        
        {/* View de solicitação de redefinição de senha */}
        {currentView === 'resetPassword' && (
          <form onSubmit={handleRequestPasswordReset}>
            <p className="text-gray-600 mb-4">Informe seu e-mail cadastrado para receber uma senha temporária.</p>
            <input
              type="email"
              placeholder="E-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border rounded mb-4"
              required
              disabled={loading}
            />
            <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white p-3 rounded font-semibold hover:bg-purple-700 disabled:bg-purple-300">
              {loading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Enviando senha temporária...
                </div>
              ) : 'Enviar Senha Temporária'}
            </button>
            {loading && (
              <p className="text-center mt-3 text-sm text-gray-600">
                O processo pode levar alguns segundos. Por favor, aguarde.
              </p>
            )}
            <p className="text-center mt-4">
              Lembrou da senha?{' '}
              <button
                type="button"
                onClick={() => {
                  setCurrentView('login');
                  setError('');
                  setSuccessMessage('');
                }}
                className="text-purple-600 font-semibold"
                disabled={loading}
              >
                Voltar para o Login
              </button>
            </p>
          </form>
        )}

        {/* View de confirmação de redefinição de senha */}
        {currentView === 'confirmResetPassword' && (
          <form onSubmit={handleConfirmPasswordReset}>
            <p className="text-gray-600 mb-4">Informe a senha temporária recebida por e-mail e defina uma nova senha.</p>
            <input
              type="email"
              placeholder="E-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border rounded mb-4"
              required
            />
            <input
              type="text"
              placeholder="Senha temporária"
              value={temporaryPassword}
              onChange={(e) => setTemporaryPassword(e.target.value)}
              className="w-full p-3 border rounded mb-4"
              required
            />
            <input
              type="password"
              placeholder="Nova senha"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full p-3 border rounded mb-4"
              required
            />
            <input
              type="password"
              placeholder="Confirmar nova senha"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              className="w-full p-3 border rounded mb-4"
              required
            />
            <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white p-3 rounded font-semibold hover:bg-purple-700 disabled:bg-purple-300">
              {loading ? 'Processando...' : 'Definir Nova Senha'}
            </button>
            <p className="text-center mt-4">
              <button
                type="button"
                onClick={() => {
                  setCurrentView('resetPassword');
                  setError('');
                  setSuccessMessage('');
                }}
                className="text-purple-600 font-semibold"
              >
                Voltar
              </button>
            </p>
          </form>
        )}

        {/* View de login */}
        {currentView === 'login' && (
          <form onSubmit={handleLogin}>
            <p className="text-gray-600 mb-4 text-center">Acesse sua conta gratuita</p>
            <input 
              type="email" 
              placeholder="E-mail" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border rounded mb-4"
              required 
            />
            <input 
              type="password" 
              placeholder="Senha" 
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              className="w-full p-3 border rounded mb-4"
              required 
            />
            <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white p-3 rounded font-semibold hover:bg-purple-700 disabled:bg-purple-300">
              {loading ? 'Aguarde...' : 'Entrar'}
            </button>
            {/* Link para recuperar senha */}
            <p className="text-center mt-3">
              <button 
                type="button"
                onClick={() => {
                  setCurrentView('resetPassword');
                  setError('');
                  setSuccessMessage('');
                }}
                className="text-purple-600 text-sm font-semibold"
              >
                Esqueceu sua senha?
              </button>
            </p>
          </form>
        )}

        {/* View de verificação de email para registro */}
        {currentView === 'verifyEmail' && (
          <form onSubmit={handleVerifyAndRegister}>
            {!email ? (
              <div>
                <p className="text-red-500 mb-4">Sessão expirada. Por favor, cadastre-se novamente.</p>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentView('register');
                    setError('');
                    setSuccessMessage('');
                    // Limpar o email do sessionStorage quando voltar ao cadastro
                    sessionStorage.removeItem('verifyEmail');
                  }}
                  className="w-full bg-purple-600 text-white p-3 rounded font-semibold hover:bg-purple-700"
                >
                  Voltar para Cadastro
                </button>
              </div>
            ) : (
              <>
                <p className="text-gray-600 mb-4">Enviamos um código de 6 dígitos para <strong>{email}</strong></p>
                <input
                  type="text"
                  placeholder="Código de verificação"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full p-3 border rounded mb-4 text-center text-xl tracking-widest"
                  maxLength="6"
                  required
                />
                <button type="submit" disabled={loading || verificationCode.length !== 6} className="w-full bg-purple-600 text-white p-3 rounded font-semibold hover:bg-purple-700 disabled:bg-purple-300">
                  {loading ? 'Verificando...' : 'Verificar e Criar Conta'}
                </button>
                <p className="text-center mt-4">
                  Não recebeu o código?{' '}
                  <button
                    type="button"
                    onClick={async () => {
                      setLoading(true);
                      setError('');
                      setSuccessMessage('');

                      try {
                        const { data } = await api.post('/api/users/register/request', { nome, email, senha }, {
                          timeout: 15000
                        });

                        // Verifica se há um código de verificação na resposta (para desenvolvimento)
                        if (data.verificationCode) {
                          setVerificationCode(data.verificationCode);
                          setSuccessMessage('Código de verificação gerado. Verifique o código abaixo e digite no campo.');
                        } else {
                          setSuccessMessage(data.message);
                        }

                        // Atualizar o email no sessionStorage ao reenviar código
                        sessionStorage.setItem('verifyEmail', email);
                      } catch (err) {
                        const errorResponse = err.response?.data;
                        let errorMessage = errorResponse?.message || 'Ocorreu um erro. Tente novamente.';

                        // Handle specific error codes from backend
                        if (errorResponse?.error) {
                          switch (errorResponse.error) {
                            case 'USER_EXISTS':
                              errorMessage = 'Utilizador já existe com este e-mail.';
                              break;
                            case 'MISSING_FIELDS':
                              errorMessage = 'Por favor, preencha todos os campos (nome, email, senha).';
                              break;
                            default:
                              errorMessage = errorResponse.message || 'Ocorreu um erro. Tente novamente.';
                          }
                        }

                        setError(errorMessage);
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                    className="text-purple-600 font-semibold disabled:opacity-50"
                  >
                    {loading ? 'Reenviando...' : 'Solicitar novamente'}
                  </button>
                </p>
              </>
            )}
          </form>
        )}

        {/* View de registro */}
        {currentView === 'register' && (
          <form onSubmit={handleSubmitRegistrationRequest}>
            <p className="text-gray-600 mb-4 text-center">Crie sua conta gratuitamente e comece a estudar agora mesmo!</p>
            <input
              type="text"
              placeholder="Nome"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full p-3 border rounded mb-4"
              required
            />
            <input
              type="email"
              placeholder="E-mail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 border rounded mb-4"
              required
            />
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="useInviteCode"
                checked={useInviteCode}
                onChange={(e) => setUseInviteCode(e.target.checked)}
                className="mr-2"
              />
              <label htmlFor="useInviteCode" className="text-gray-700">Usar código de convite</label>
            </div>

            {useInviteCode && (
              <input
                type="text"
                placeholder="Código de convite (opcional)"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                className="w-full p-3 border rounded mb-4"
                maxLength="20"
              />
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800">
              <p><strong>Importante:</strong> Após solicitar o cadastro, você receberá uma senha temporária no seu e-mail. Use essa senha para fazer login.</p>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white p-3 rounded font-semibold hover:bg-purple-700 disabled:bg-purple-300">
              {loading ? 'Enviando...' : 'Solicitar Cadastro'}
            </button>
          </form>
        )}

        {/* Links de navegação entre views */}
        <p className="text-center mt-4">
          {currentView === 'login' && (
            <>
              Não tem uma conta?{' '}
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setCurrentView('register');
                  setError('');
                  setSuccessMessage('');
                }}
                className="text-purple-600 font-semibold"
              >
                Cadastre-se
              </button>
            </>
          )}
          {currentView === 'register' && (
            <>
              Já tem uma conta?{' '}
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setCurrentView('login');
                  setError('');
                  setSuccessMessage('');
                }}
                className="text-purple-600 font-semibold"
              >
                Faça login
              </button>
            </>
          )}
          {currentView === 'verifyEmail' && (
            <>
              Voltar para{' '}
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setCurrentView('register');
                  setError('');
                  setSuccessMessage('');
                  // Limpar o email do sessionStorage quando voltar ao cadastro
                  sessionStorage.removeItem('verifyEmail');
                }}
                className="text-purple-600 font-semibold"
              >
                Cadastro
              </button>
            </>
          )}
          {currentView === 'resetPassword' && (
            <>
              {/* No additional navigation link needed here, as there's a "Voltar para o Login" button above */}
            </>
          )}
          {currentView === 'confirmResetPassword' && (
            <>
              {/* Navigation is handled by the "Voltar" button in the form */}
            </>
          )}
        </p>
        
        {/* Link para Política de Privacidade */}
        {currentView === 'register' && (
          <p className="text-center mt-3 text-sm text-gray-600">
            Ao criar uma conta, você concorda com nossas{' '}
            <button
              type="button"
              onClick={() => window.open('/politica-privacidade', '_blank')}
              className="text-purple-600 font-semibold hover:text-purple-800"
            >
              Políticas de Privacidade
            </button>
          </p>
        )}
      </div>
    </div>
  );
};

// Componente principal da Landing Page
const LandingPage = ({ onLogin }) => {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [initialView, setInitialView] = useState('login'); // 'login' ou 'register'
  const [selectedCard, setSelectedCard] = useState(null);

  const openModal = (initialViewParam) => {
    setInitialView(initialViewParam);
    setShowAuthModal(true);
  };

  const handleCardClick = (cardType) => {
    // Define os dados do card com base no tipo
    const cardData = {
      catalogo: {
        type: 'catalogo',
        title: 'Catálogo de Matérias',
        description: 'Acesse todos os conteúdos'
      },
      progresso: {
        type: 'progresso',
        title: 'Meu Progresso',
        description: 'Acompanhe sua evolução'
      },
      flashcards: {
        type: 'flashcards',
        title: 'Flashcards',
        description: 'Estude com cartões de memória'
      },
      conecta: {
        type: 'conecta',
        title: 'Conecta',
        description: 'Conecte-se com outros estudantes'
      },
    };

    setSelectedCard(cardData[cardType]);
  };

  const getCardIcon = (card) => {
    // Retorna o SVG com base no tipo de card
    const icons = {
      catalogo: (
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
        </svg>
      ),
      progresso: (
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"></path>
        </svg>
      ),
      flashcards: (
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
        </svg>
      ),
      conecta: (
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 1 1 -6 0 3 3 0 0 1 6 0zm6 3a2 2 0 1 1 -4 0 2 2 0 0 1 4 0zM7 10a2 2 0 1 1 -4 0 2 2 0 0 1 4 0z"></path>
        </svg>
      ),
    };

    return icons[card.type] || icons.catalogo; // Retorna o ícone de catálogo como fallback
  };

  const getCardContent = (card) => {
    // Retorna o conteúdo correspondente ao card selecionado
    if (card.type === 'progresso') {
      // Meu Progresso
      return (
        <div className="bg-white border border-violet-300 rounded-xl p-6 flex flex-col justify-between h-52">
          <div className="flex justify-between h-full">
            {/* Texto (esquerda) */}
            <div className="flex-1 pr-6 flex flex-col justify-between">
              <h3 className="text-xl font-semibold text-violet-800">Meu Progresso</h3>
              <div className="mt-2 space-y-2">
                <div className="h-1 w-4/5 bg-violet-200 rounded"></div>
                <div className="h-1 w-3/4 bg-violet-200 rounded"></div>
                <div className="h-1 w-1/2 bg-violet-200 rounded"></div>
                <div className="h-1 w-2/3 bg-violet-200 rounded"></div>
              </div>
            </div>

            {/* Gráfico fake (direita) */}
            <div className="w-40 flex items-end justify-center">
              <div className="w-full h-32 border-l border-b border-violet-500 flex items-end justify-around">
                <div className="w-4 h-8 bg-violet-300"></div>
                <div className="w-4 h-20 bg-violet-500"></div>
                <div className="w-4 h-14 bg-violet-400"></div>
              </div>
            </div>
          </div>

          {/* indicadores (bolinhas) */}
          <div className="flex justify-center mt-4 space-x-2">
            <span className="w-2 h-2 rounded-full bg-violet-200"></span>
            <span className="w-2 h-2 rounded-full bg-violet-500"></span>
            <span className="w-2 h-2 rounded-full bg-violet-200"></span>
          </div>
        </div>
      );
    } else {
      // Para outros cards, mostrar uma mensagem genérica ou o box correspondente
      return (
        <div className="bg-white border border-violet-300 rounded-xl p-6 flex flex-col justify-between h-52 flex items-center justify-center">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-violet-800 mb-2">{card.title}</h3>
            <p className="text-violet-600">{card.description}</p>
            <p className="mt-4 text-sm text-violet-500">Conteúdo em desenvolvimento</p>
          </div>
        </div>
      );
    }
  };

  return (
    <div className="bg-white font-sans">
      <AuthErrorBoundary>
        <AuthModal
          show={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onLoginSuccess={onLogin}
          initialView={initialView}
        />
      </AuthErrorBoundary>

      {/* Header */}
      <header className="py-4 px-6 md:px-12 flex justify-between items-center border-b sticky top-0 bg-white z-10">
        <div className="flex items-center">
          <img src="/logo-v.png" alt="Vestibula" className="w-10 h-10 mr-3" fetchPriority="high" />
          <span className="text-xl font-bold text-gray-800">Vestibula</span>
        </div>
        <div>
          <button type="button" onClick={() => openModal('login')} className="text-purple-600 font-semibold mr-4 hover:text-purple-800">Login</button>
          <button type="button" onClick={() => openModal('register')} className="bg-purple-600 text-white font-semibold px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-transform transform hover:scale-105">Cadastro</button>
        </div>
      </header>

      {/* Nova seção de headline */}
      <section className="bg-gradient-to-r from-purple-600 to-indigo-700 py-20 px-6 text-center">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Chegou a sua vez de passar no ENEM!
          </h1>
          <p className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto text-white opacity-90">
            Aproveite a nossa plataforma 100% gratuita e se prepare para o vestibular sem sair de casa.
          </p>
          <button
            type="button"
            onClick={() => openModal('register')}
            className="bg-yellow-500 hover:bg-yellow-400 text-purple-900 font-bold px-8 py-4 rounded-lg text-lg transition-transform transform hover:scale-105 shadow-lg"
          >
            Fazer Parte
          </button>
        </div>
      </section>

      {/* Features Section - Destacando os recursos solicitados */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-16">
            Por que estudar com o Vestibula?
          </h2>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="flex flex-col items-center p-6 bg-purple-50 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <FeatureIcon>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path></svg>
              </FeatureIcon>
              <h3 className="mt-6 font-bold text-xl text-gray-900">Acompanhe seu Progresso</h3>
              <p className="mt-4 text-gray-600">Visualize sua evolução com gráficos e em tempo real.</p>
            </div>
            <div className="flex flex-col items-center p-6 bg-purple-50 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <FeatureIcon>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 1 1 -6 0 3 3 0 0 1 6 0zm6 3a2 2 0 1 1 -4 0 2 2 0 0 1 4 0zM7 10a2 2 0 1 1 -4 0 2 2 0 0 1 4 0z"></path></svg>
              </FeatureIcon>
              <h3 className="mt-6 font-bold text-xl text-gray-900">Comunidade "Conecta"</h3>
              <p className="mt-4 text-gray-600">Conecte-se com outros estudantes, tire dúvidas e forme grupos de estudo.</p>
            </div>
            <div className="flex flex-col items-center p-6 bg-purple-50 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <FeatureIcon>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                </svg>
              </FeatureIcon>
              <h3 className="mt-6 font-bold text-xl text-gray-900">Flashcards</h3>
              <p className="mt-4 text-gray-600">Reforce o aprendizado com cartões de estudo inteligentes.</p>
            </div>
          </div>
        </div>
      </section>


      {/* Seção de Notificações - Demonstrando o interior da plataforma */}
      <section className="bg-gray-50 py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-16">
            Estudo com o nosso sistema de notificação inteligente
          </h2>
          <div className="bg-white rounded-lg p-4 max-h-96 overflow-y-auto pr-2 space-y-3 shadow-inner border border-gray-200 purple-scrollbar">
            <div className="bg-violet-100 border-l-4 border-violet-500 p-4 rounded-r-lg flex items-center gap-4">
              <svg className="w-6 h-6 text-violet-700 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
              </svg>
              <p className="text-violet-800 font-medium">Dica do dia: Não se esqueça de assistir os conteúdos para poder revisar!</p>
            </div>
            <div className="bg-violet-200 border-l-4 border-violet-500 p-4 rounded-r-lg flex items-center gap-4">
              <svg className="w-6 h-6 text-violet-800 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div className="flex-1">
                <p className="text-violet-800 font-medium">Revisão de 1 dia para "Fonética e Fonologia" está disponível.</p>
              </div>
              <button className="text-violet-800 hover:text-violet-900 font-semibold text-sm">Marcar como revisado</button>
            </div>
            <div className="bg-violet-200 border-l-4 border-violet-500 p-4 rounded-r-lg flex items-center gap-4">
              <svg className="w-6 h-6 text-violet-800 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div className="flex-1">
                <p className="text-violet-800 font-medium">Revisão de 7 dias para "Divisão de Números Naturais" está disponível.</p>
              </div>
              <button className="text-violet-800 hover:text-violet-900 font-semibold text-sm">Marcar como revisado</button>
            </div>
            <div className="bg-violet-200 border-l-4 border-violet-500 p-4 rounded-r-lg flex items-center gap-4">
              <svg className="w-6 h-6 text-violet-800 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div className="flex-1">
                <p className="text-violet-800 font-medium">Revisão de 7 dias para "Introdução à bioquímica" está disponível.</p>
              </div>
              <button className="text-violet-800 hover:text-violet-900 font-semibold text-sm">Marcar como revisado</button>
            </div>
            <div className="bg-violet-200 border-l-4 border-violet-500 p-4 rounded-r-lg flex items-center gap-4">
              <svg className="w-6 h-6 text-violet-800 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <div className="flex-1">
                <p className="text-violet-800 font-medium">Revisão de 30 dias para "Tabela Periódica e Propriedades" está disponível.</p>
              </div>
              <button className="text-violet-800 hover:text-violet-900 font-semibold text-sm">Marcar como revisado</button>
            </div>
          </div>
        </div>
      </section>


      {/* Call to Action Final */}
      <section className="bg-gradient-to-r from-purple-600 to-indigo-700 py-20 px-6 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Comece agora e conquiste sua vaga!
          </h2>
          <p className="text-xl mb-10">
            Cadastre-se gratuitamente e tenha acesso ao catálogo completo de matérias.
          </p>
          <button type="button" onClick={() => openModal('register')} className="bg-yellow-500 hover:bg-yellow-400 text-purple-900 font-bold px-8 py-4 rounded-lg text-lg transition-transform transform hover:scale-105 shadow-lg">
            Criar conta gratuita
          </button>
          <p className="mt-6 text-purple-200">
            Já tem uma conta? <button type="button" onClick={() => openModal('login')} className="underline font-semibold">Faça login</button>
          </p>
        </div>
      </section>

      

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 px-6 text-center">
        <p>&copy; 2025 Vestibula. Todos os direitos reservados.</p>
        {/* Link para Política de Privacidade */}
        <p className="mt-2 text-sm text-gray-400">
          Ao criar uma conta, você concorda com nossas{' '}
          <button 
            onClick={() => window.open('/politica-privacidade', '_blank')}
            className="text-purple-300 hover:text-white underline"
          >
            Políticas de Privacidade
          </button>
        </p>
      </footer>
    </div>
  );
};

export default LandingPage;
